/**
 * Pure helpers for ManageAssessments admin form: flags, hydration, save pieces,
 * and date-row / observation field config used by the UI.
 */

import {
  ASSESSMENT_FIELD_LABELS,
  buildContactsNotesPayload,
  resolvePrimarySponsorContactContext,
} from "./assessmentAdminFieldConfig";

/** Coerce checkbox / RHF boolean-or-string flags. */
export function isTruthyFlag(value) {
  return value === true || value === "true";
}

/**
 * Effective "intro meeting not required" for display / picker visibility.
 * When the form value is still undefined, fall back to the server flag.
 */
export function resolveIntroMeetingNotRequiredEffective(
  formValue,
  serverNotRequired
) {
  if (isTruthyFlag(formValue)) return true;
  if (formValue === false || formValue === "false") return false;
  if (formValue === undefined) return serverNotRequired === true;
  return false;
}

/**
 * Resolve introMeetingNotRequired for Save (form + watched fallback).
 */
export function resolveIntroMeetingNotRequiredForSave(
  formValue,
  watchedValue
) {
  return (
    isTruthyFlag(formValue) ||
    watchedValue === true ||
    isTruthyFlag(watchedValue)
  );
}

/**
 * Intro meeting date for payload: clear when not required.
 */
export function resolveIntroMeetingDateForSave({
  introMeetingNotRequired,
  formStartDate,
  fallbackDate,
}) {
  if (introMeetingNotRequired) return null;
  return formStartDate || fallbackDate || null;
}

/**
 * New admin fields hydrated from VQA details for status-only / non-form saves.
 * @param {object|null|undefined} vqa - vqaDetails.vqa
 * @param {{ requestContacts?: Array, contactNotesByEmail?: Record<string, string>, sponsorContacts?: Array, sponsorContactNotesByEmail?: Record<string, string>, sponsorContactsSponsorId?: string|null }} [opts]
 */
export function buildAdminExtendedFieldsFromVqa(
  vqa,
  {
    requestContacts,
    contactNotesByEmail,
    sponsorContacts,
    sponsorContactNotesByEmail,
    sponsorContactsSponsorId,
  } = {}
) {
  const introMeetingNotRequired = vqa?.intro_meeting_not_required === true;
  return {
    introMeetingNotRequired,
    introMeetingDate: introMeetingNotRequired
      ? null
      : (vqa?.intro_meeting_complete_date ?? null),
    firstDraftDeliveredByAuditorDate:
      vqa?.first_draft_delivered_by_auditor_on ?? null,
    finalDraftDeliveredByAuditorDate:
      vqa?.final_draft_delivered_by_auditor_on ?? null,
    observationResponseReceivedDate:
      vqa?.observation_response_received_on ?? null,
    observationReportCompletedDate:
      vqa?.observation_report_completed_on ?? null,
    publishedToLibraryDate: vqa?.published_to_library_on ?? null,
    diligentNotes: vqa?.diligent_notes ?? null,
    provider_contacts: buildContactsNotesPayload(
      requestContacts,
      contactNotesByEmail
    ),
    sponsor_contacts_sponsor_id: sponsorContactsSponsorId ?? null,
    sponsor_contacts: buildContactsNotesPayload(
      sponsorContacts,
      sponsorContactNotesByEmail
    ),
  };
}

/**
 * New admin fields from form submit data.
 * @param {object} data - RHF submit values
 * @param {{
 *   watchedIntroMeetingNotRequired?: boolean|string,
 *   fallbackIntroMeetingDate?: string|null,
 *   requestContacts?: Array,
 *   contactNotesByEmail?: Record<string, string>,
 *   sponsorContacts?: Array,
 *   sponsorContactNotesByEmail?: Record<string, string>,
 *   sponsorContactsSponsorId?: string|null,
 * }} [opts]
 */
export function buildAdminExtendedFieldsFromForm(
  data,
  {
    watchedIntroMeetingNotRequired,
    fallbackIntroMeetingDate,
    requestContacts,
    contactNotesByEmail,
    sponsorContacts,
    sponsorContactNotesByEmail,
    sponsorContactsSponsorId,
  } = {}
) {
  const introMeetingNotRequired = resolveIntroMeetingNotRequiredForSave(
    data?.introMeetingNotRequired,
    watchedIntroMeetingNotRequired
  );
  return {
    introMeetingNotRequired,
    introMeetingDate: resolveIntroMeetingDateForSave({
      introMeetingNotRequired,
      formStartDate: data?.introMeetingDate?.startDate || null,
      fallbackDate: fallbackIntroMeetingDate,
    }),
    firstDraftDeliveredByAuditorDate:
      data?.firstDraftDeliveredByAuditorDate?.startDate || null,
    finalDraftDeliveredByAuditorDate:
      data?.finalDraftDeliveredByAuditorDate?.startDate || null,
    observationResponseReceivedDate:
      data?.observationResponseReceivedDate?.startDate || null,
    observationReportCompletedDate:
      data?.observationReportCompletedDate?.startDate || null,
    publishedToLibraryDate:
      data?.publishedToLibraryDate?.startDate || null,
    diligentNotes: data?.diligentNotes ?? null,
    provider_contacts: buildContactsNotesPayload(
      requestContacts,
      contactNotesByEmail
    ),
    sponsor_contacts_sponsor_id: sponsorContactsSponsorId ?? null,
    sponsor_contacts: buildContactsNotesPayload(
      sponsorContacts,
      sponsorContactNotesByEmail
    ),
  };
}

/** @deprecated use resolvePrimarySponsorContactContext from assessmentAdminFieldConfig */
export { resolvePrimarySponsorContactContext };

/**
 * Form defaults / setValue targets for the new admin fields from VQA.
 */
export function hydrateAdminExtendedFormValues(vqa) {
  return {
    introMeetingNotRequired: vqa?.intro_meeting_not_required === true,
    diligentNotes: vqa?.diligent_notes || "",
  };
}

/** Display name for a request contact row. */
export function formatVendorContactDisplayName(contact) {
  return [contact?.first_name, contact?.last_name]
    .filter(Boolean)
    .join(" ")
    .trim();
}

/**
 * Notes text to show in read-only mode (local draft overrides server).
 */
export function resolveVendorContactNotesDisplay(
  emailKey,
  contactNotesByEmail,
  contact
) {
  const local = contactNotesByEmail?.[emailKey];
  if (local !== undefined && local !== "") return local;
  if (local === "") return "";
  return contact?.notes || "";
}

/**
 * Extra admin date rows (final draft + observation) for config-driven UI.
 * `section`: "main" | "observation"
 */
export const ASSESSMENT_ADMIN_EXTRA_DATE_ROWS = [
  {
    id: "firstDraftDeliveredByAuditor",
    labelKey: "firstDraftDeliveredByAuditor",
    fieldName: "firstDraftDeliveredByAuditorDate",
    vqaKey: "first_draft_delivered_by_auditor_on",
    section: "main",
  },
  {
    id: "finalDraftDeliveredByAuditor",
    labelKey: "finalDraftDeliveredByAuditor",
    fieldName: "finalDraftDeliveredByAuditorDate",
    vqaKey: "final_draft_delivered_by_auditor_on",
    section: "main",
  },
  {
    id: "observationResponseReceived",
    labelKey: "observationResponseReceived",
    fieldName: "observationResponseReceivedDate",
    vqaKey: "observation_response_received_on",
    section: "observation",
  },
  {
    id: "observationReportCompleted",
    labelKey: "observationReportCompleted",
    fieldName: "observationReportCompletedDate",
    vqaKey: "observation_report_completed_on",
    section: "observation",
  },
];

export function getAssessmentAdminExtraDateRows(section) {
  return ASSESSMENT_ADMIN_EXTRA_DATE_ROWS.filter(
    (row) => row.section === section
  );
}

export function getAdminExtraDateRowLabel(row) {
  return ASSESSMENT_FIELD_LABELS[row.labelKey] ?? row.labelKey;
}

export function getVqaDateFieldValue(vqa, vqaKey) {
  return vqa?.[vqaKey] ?? null;
}

/**
 * Format a date string for display; invalid parses become "-".
 */
export function formatMaybeInvalidDate(value, dateFormat) {
  if (!value) return "-";
  if (Number.isNaN(Date.parse(value))) return "-";
  return dateFormat(value);
}

/**
 * Post–agreed-draft admin rows (system-managed + sponsor draft delivered).
 */
export const ASSESSMENT_ADMIN_SYSTEM_MANAGED_DATE_ROWS = [
  {
    id: "sponsorDraftUploaded",
    labelKey: "sponsorDraftUploaded",
    vqaKey: "first_draft_report_uploaded_by_auditor_on",
    editable: false,
  },
  {
    id: "sponsorDraftReportDelivered",
    labelKey: "sponsorDraftReportDelivered",
    fieldName: "firstDraftDelieveredSponsorDate",
    vqaKey: "first_draft_report_delivered_to_sponsor_on",
    editable: true,
  },
  {
    id: "sponsorDownloadOfDraftReport",
    labelKey: "sponsorDownloadOfDraftReport",
    vqaKey: "draft_review_date",
    editable: false,
  },
  {
    id: "finalSponsorReportUploaded",
    labelKey: "finalSponsorReportUploaded",
    vqaKey: "final_report_delivered_to_sponsor_on",
    editable: false,
  },
  {
    id: "publishedToLibrary",
    labelKey: "publishedToLibrary",
    fieldName: "publishedToLibraryDate",
    vqaKey: "published_to_library_on",
    editable: true,
  },
];

export function getAssessmentAdminSystemManagedDateRows() {
  return ASSESSMENT_ADMIN_SYSTEM_MANAGED_DATE_ROWS;
}

/**
 * Apply hydrated extended fields when entering edit mode.
 */
export function applyAdminExtendedValuesOnEditEnter(setValue, vqa) {
  if (!vqa || typeof setValue !== "function") return;
  const hydrated = hydrateAdminExtendedFormValues(vqa);
  setValue("introMeetingNotRequired", hydrated.introMeetingNotRequired, {
    shouldValidate: false,
  });
  setValue("diligentNotes", hydrated.diligentNotes, {
    shouldValidate: false,
  });
}

/**
 * Read-only intro meeting cell content key for branching in the UI.
 * @returns {"notRequired"|"date"|"empty"}
 */
export function getIntroMeetingReadDisplayKind(vqa) {
  if (vqa?.intro_meeting_not_required === true) return "notRequired";
  if (vqa?.intro_meeting_complete_date != null) return "date";
  return "empty";
}
